import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Calendar, Lock, UserCircle2, UserPlus } from "lucide-react";
import { producersMgmtApi } from "./api";
import type { ProducerListItem, ProducerListStatus } from "./types";
import { RegisterProducerModal } from "./RegisterProducerModal";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useDebounce } from "@/lib/utils/useDebounce";
import { Can, usePermission } from "@/lib/permissions/useCan";
import { recruitersAdminApi } from "../recruiters/api";
import type { RecruiterAssignment } from "../recruiters/types";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const STATUS_OPTIONS: { value: ProducerListStatus | ""; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "active", label: "Activos" },
  { value: "blocked", label: "Bloqueados" },
  { value: "deleted", label: "Borrados (soft)" },
];

const PAGE_SIZE = 25;

export function ProducersScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ProducerListStatus | "">("");
  const [page, setPage] = useState(1);
  const [showRegister, setShowRegister] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const query = useQuery({
    queryKey: [
      "producers-mgmt",
      "list",
      { q: debouncedSearch, status, page },
    ],
    queryFn: () =>
      producersMgmtApi.list({
        q: debouncedSearch || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  // Reclutador asignado (pipeline o activo). Sólo para quien puede ver Reclutadores.
  const canSeeRecruiters = usePermission(PERMISSIONS.RECRUITERS_VIEW);
  const assignmentsQuery = useQuery({
    queryKey: ["recruiters", "assignments"],
    queryFn: recruitersAdminApi.assignments,
    enabled: canSeeRecruiters,
    staleTime: 60_000,
  });
  const recruiterOf = useMemo(() => {
    const byUser = new Map<number, RecruiterAssignment>();
    const byEmail = new Map<string, RecruiterAssignment>();
    for (const a of assignmentsQuery.data?.assignments ?? []) {
      if (a.producerUserId) byUser.set(a.producerUserId, a);
      if (a.contactEmail) byEmail.set(a.contactEmail.toLowerCase(), a);
    }
    return (p: ProducerListItem) => byUser.get(p.id) ?? byEmail.get(p.email.toLowerCase()) ?? null;
  }, [assignmentsQuery.data]);

  const columns: Column<ProducerListItem>[] = [
    {
      key: "user",
      header: "Productor",
      cell: (p) => (
        <div className="flex items-center gap-2.5 min-w-0">
          {p.img ? (
            <img
              src={p.img}
              alt=""
              className="w-8 h-8 rounded-full object-cover border border-border"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-fg-muted">
              <UserCircle2 size={16} />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-fg truncate">
              {[p.firstName, p.lastName].filter(Boolean).join(" ") ||
                p.userName ||
                p.email}
            </div>
            <div className="text-xs text-fg-subtle truncate">{p.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "flags",
      header: "Estado",
      cell: (p) => (
        <div className="flex flex-wrap gap-1.5">
          {p.deletedAt && (
            <span className="nx-badge bg-danger-soft text-danger">Borrado</span>
          )}
          {p.isBlocked && (
            <span className="nx-badge bg-danger-soft text-danger">
              <Lock size={10} className="inline mr-0.5" /> Bloqueado
            </span>
          )}
          {!p.deletedAt && !p.isBlocked && (
            <span className="nx-badge bg-success-soft text-success">Activo</span>
          )}
          {p.isArtist && (
            <span className="nx-badge bg-accent-soft text-accent">Artista</span>
          )}
          {p.isPartner && (
            <span className="nx-badge bg-accent-soft text-accent">Partner</span>
          )}
        </div>
      ),
    },
    ...(canSeeRecruiters
      ? [
          {
            key: "recruiter",
            header: "Reclutador",
            cell: (p: ProducerListItem) => {
              const a = recruiterOf(p);
              if (!a) return <span className="text-sm text-fg-subtle">—</span>;
              return (
                <Link
                  to={`/reclutadores/${a.recruiterId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-accent hover:underline"
                  title={a.registeredOn ? `Atribución desde el ${a.registeredOn}` : undefined}
                >
                  {a.recruiterName}
                  <span className="text-2xs text-fg-subtle ml-1">{a.status === "active" ? "activo" : "en pipeline"}</span>
                </Link>
              );
            },
          } as Column<ProducerListItem>,
        ]
      : []),
    {
      key: "clubs",
      header: "Clubs",
      align: "right",
      width: "min",
      cell: (p) => (
        <span className="tabular-nums text-sm text-fg inline-flex items-center gap-1">
          <Building2 size={12} className="text-fg-subtle" />
          {p.clubCount}
        </span>
      ),
    },
    {
      key: "events",
      header: "Eventos",
      align: "right",
      width: "min",
      cell: (p) => (
        <span className="tabular-nums text-sm text-fg inline-flex items-center gap-1">
          <Calendar size={12} className="text-fg-subtle" />
          {p.eventCount}
        </span>
      ),
    },
    {
      key: "created",
      header: "Registrado",
      align: "right",
      width: "min",
      cell: (p) => (
        <span className="text-xs text-fg-subtle whitespace-nowrap">
          {formatDateTime(p.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="nx-h1">Productores</h1>
          <p className="nx-eyebrow text-fg-muted mt-1">
            Gestiona cuentas, clubs y eventos. Ideal para QA/pruebas mientras
            la arquitectura sigue cambiando.
          </p>
        </div>
        <Can permission={PERMISSIONS.PRODUCERS_MGMT_EDIT}>
          <button
            type="button"
            onClick={() => setShowRegister(true)}
            className="nx-btn-primary shrink-0"
          >
            <UserPlus size={14} />
            Registrar productor
          </button>
        </Can>
      </div>

      <FiltersBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Buscar por email, nombre o usuario…"
        status={status}
        onStatusChange={(v) => {
          setStatus(v as ProducerListStatus | "");
          setPage(1);
        }}
        statusOptions={STATUS_OPTIONS}
      />

      {query.isLoading && <LoadingBlock label="Cargando productores…" />}
      {query.isError && <ErrorBlock onRetry={() => query.refetch()} />}
      {query.data && (
        <ApplicationsTable
          rows={query.data.producers}
          columns={columns}
          page={query.data.page}
          pageSize={query.data.pageSize}
          total={query.data.total}
          onPageChange={setPage}
          onRowClick={(p) => navigate(`/producers/${p.id}`)}
          emptyLabel="No hay productores que coincidan con los filtros."
        />
      )}

      {showRegister && (
        <RegisterProducerModal onClose={() => setShowRegister(false)} />
      )}
    </div>
  );
}
